import { create } from "zustand";
import {
	appSettingsConfig,
	loanFieldsMap,
	stepProcessMap,
	subLoanFieldsMap,
} from "@/lib/constants";
import { loan_type } from "@/lib/constants";

export interface LoanType {
	option: string;
	id: number;
	value: string;
}

export interface FormState {
	formError: boolean;
	selectedIndex: number;
	isLoading: boolean;
	isPhoneVerified: boolean;
	enabledFields: Record<string, any[]>;
	enabledBusinessFields: Record<string, any[]>;
	enabledLoans: LoanType[];
	loanAmountMin: number;
	combinedIncomeMin: number;
	isSubmitted: boolean;
	validateFormDetails?: () => boolean;
	appSettings: Record<string, string>;
	formContainer: number;
	toggleTC: boolean;
	stepProcess: Record<string, any>;
	hasAttemptedSubmit: boolean;
	purchasePriceMin: number;
	depositAmountMin: number;

	// Setter functions
	setFormErrors: (hasError: boolean) => void;
	setSelectedIndex: (index: number) => void;
	setLoading: (loading: boolean) => void;
	setPhoneVerified: (isVerified: boolean) => void;
	setEnabledFields: (loanType: string) => void;
	setEnabledBusinessFields: (loanPurpose: string) => void;
	setLoanAmountMin: (loanAmountMin: number) => void;
	setcombinedIncomeMin: (combinedIncomeMin: number) => void;
	setEnabledLoans: (loanTypes: string[]) => void;
	setIsSubmitted: (isSubmitted: boolean) => void;
	setValidateFormDetails: (fn: () => boolean) => void;
	setAppSettings: (appSettings: string) => void;
	setFormContainer: (size: number) => void;
	setToggleTC: (toggle: boolean) => void;
	setStepProcess: (step: string) => void;
	setHasAttemptedSubmit: (value: boolean) => void;
	setPurchasePriceMin: (purchasePricetMin: number) => void;
	setDepositAmountMin: (purchasePricetMin: number) => void;
}

export const useFormState = () =>
	create<FormState>((set) => ({
		formError: false,
		selectedIndex: 0,
		isLoading: false,
		isPhoneVerified: false,
		enabledFields: {}, // Initially empty but will update dynamically
		enabledBusinessFields: {}, // Initially empty but will update dynamically
		enabledLoans: [], // Initially empty but will update dynamically
		loanAmountMin: 10000,
		combinedIncomeMin: 10000,
		isSubmitted: false,
		appSettings: {},
		formContainer: 0,
		toggleTC: false,
		stepProcess: {},
		hasAttemptedSubmit: false,
		purchasePriceMin: 50000,
		depositAmountMin: 2500,

		setFormErrors: (hasError) => set(() => ({ formError: hasError })),
		setSelectedIndex: (index) => set(() => ({ selectedIndex: index })),
		setLoading: (loading) => set(() => ({ isLoading: loading })),
		setPhoneVerified: (isVerified) => set(() => ({ isPhoneVerified: isVerified })),
		setEnabledFields: (loanType) => {
			set({ enabledFields: loanFieldsMap[loanType] || {} });
		},
		setEnabledBusinessFields: (loanPurpose) => {
			set({ enabledBusinessFields: subLoanFieldsMap[loanPurpose] || {} });
		},
		setEnabledLoans: (loanTypes) => {
			if (!Array.isArray(loanTypes)) {
				console.warn("setEnabledLoans: Expected an array, but got: ", loanTypes);
				return;
			}

			const filteredLoans = loan_type.filter(
				(loan) => loanTypes.includes(loan.value), // Match loan types from settings
			);

			set({ enabledLoans: filteredLoans }); // Store full loan objects (option, id, value)
		},
		setLoanAmountMin: (loanAmount) => set(() => ({ loanAmountMin: loanAmount })),
		setcombinedIncomeMin: (combinedIncome) =>
			set(() => ({ combinedIncomeMin: combinedIncome })),
		setIsSubmitted: (status) => set(() => ({ isSubmitted: status })),
		setValidateFormDetails: (fn) => set(() => ({ validateFormDetails: fn })),
		setAppSettings: (siteTitle) =>
			set(() => ({ appSettings: appSettingsConfig[siteTitle] || {} })),
		setFormContainer: (size) => set(() => ({ formContainer: size })),
		setToggleTC: (toggle) => set(() => ({ toggleTC: toggle })),
		setStepProcess: (step) => {
			set({ stepProcess: stepProcessMap[step] || {} });
		},
		setHasAttemptedSubmit: (value) => set(() => ({ hasAttemptedSubmit: value })),
		setPurchasePriceMin: (purchasePrice) => set(() => ({ purchasePriceMin: purchasePrice })),
		setDepositAmountMin: (purchasePrice) => set(() => ({ purchasePriceMin: purchasePrice })),
	}));
